import { Reveal } from "./Reveal";
import { PRODUCTS } from "@/data/business";
import {
  Layers, Squircle, Droplets, ShieldCheck, Construction, Package,
  Grid3x3, Boxes, Palette, DoorOpen, TreePine, Sparkles, Frame, BookOpenText, Ruler, Beaker,
} from "lucide-react";

const ICONS = {
  Plywood: Layers,
  "Block Boards": Squircle,
  "Marine Plywood": ShieldCheck,
  "Waterproof Plywood": Droplets,
  "Shuttering Plywood": Construction,
  "Packing Plywood": Package,
  MDF: Grid3x3,
  "Particle Board": Boxes,
  "Formica Sheets": Palette,
  "Flush Doors": DoorOpen,
  Veneers: TreePine,
  "PVC Sheets": Sparkles,
  Doors: DoorOpen,
  Panels: Frame,
  "Edge Bands": Ruler,
  Adhesives: Beaker,
  default: BookOpenText,
};

const ProductCard = ({ name, desc, highlighted, index }) => {
  const Icon = ICONS[name] || ICONS.default;
  return (
    <Reveal delay={index * 0.04} className="h-full">
      <div
        data-testid={`product-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
        className={`group h-full flex flex-col bg-white rounded-lg border transition-all duration-500 p-6 sm:p-7 ${
          highlighted
            ? "border-[color:var(--nitc-accent)]/30 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(122,78,45,0.25)]"
            : "border-[color:var(--nitc-border)] shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
        } hover:-translate-y-1 hover:shadow-[0_8px_32px_-12px_rgba(0,0,0,0.12)] hover:border-[color:var(--nitc-accent)]/40`}
      >
        <div className="flex items-start justify-between">
          <div
            className={`h-11 w-11 rounded-md flex items-center justify-center ${
              highlighted
                ? "bg-[color:var(--nitc-accent)] text-white"
                : "bg-[color:var(--nitc-bg-3)] text-[color:var(--nitc-accent)]"
            } transition-colors`}
          >
            <Icon className="h-5 w-5" strokeWidth={1.6} />
          </div>
          {highlighted && (
            <span className="text-[10px] tracking-[0.18em] uppercase font-medium text-[color:var(--nitc-accent)]">
              Featured
            </span>
          )}
        </div>
        <h3 className="mt-6 font-display text-lg sm:text-xl font-semibold text-[color:var(--nitc-ink)]">
          {name}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-[color:var(--nitc-text)] flex-1">{desc}</p>
      </div>
    </Reveal>
  );
};

const Products = () => {
  let cardIndex = 0;
  return (
    <section id="products" data-testid="products-section" className="py-24 sm:py-32 bg-white">
      <div className="container-nitc">
        <Reveal className="max-w-3xl">
          <p className="text-eyebrow">What we deal in</p>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--nitc-ink)] leading-[1.05] tracking-tight">
            Every sheet, panel and door for the spaces you build.
          </h2>
          <p className="mt-6 text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed">
            From construction-grade shuttering ply to luxury veneers — our showroom and warehouse stock
            covers the full range of plywood, laminates and allied materials.
          </p>
        </Reveal>

        {PRODUCTS.map((group) => (
          <div key={group.group} className="mt-16 sm:mt-20">
            <Reveal>
              <div className="flex items-end justify-between mb-8">
                <h3 className="font-display text-xl sm:text-2xl font-medium text-[color:var(--nitc-ink)]">
                  {group.group}
                </h3>
                <div className="hidden sm:block flex-1 h-px bg-[color:var(--nitc-border)] mx-6" />
                <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--nitc-muted)]">
                  {group.items.length} Categories
                </span>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {group.items.map((item) => {
                const highlighted = group.group === "Plywood & Boards";
                const card = (
                  <ProductCard
                    key={item.name}
                    name={item.name}
                    desc={item.desc}
                    highlighted={highlighted}
                    index={cardIndex}
                  />
                );
                cardIndex += 1;
                return card;
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Products;
